/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BLOCK_RESTRICTED_COUNTRIES
 * Date:         2026-01-18
 * Modified By:  Cursor
 *
 * Description:
 * Filter country dropdown to exclude restricted countries (Belarus, Cuba, Iran, Iraq, 
 * North Korea, Russia) from company profile selection. Companies cannot select these 
 * countries in the country dropdown field.
 *
 * File Size Check:
 * - Current: 56 lines
 * - Adding: ~1 line (import change)
 * - Projected: ~57 lines
 * - Decision: Proceed (within 200-line limit)
 *
 * Related Files:
 * - client/src/components/setup/company-profile/components/CompanyAddress.tsx
 * - client/src/utils/countries.ts (selectableCountries export)
 * - client/src/components/setup/company-profile/utils/companyValidation.ts (validation update)
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SPLIT_COMPANY_PROFILE_ADDRESS
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted company address section from CompanyProfileForm.tsx.
 * Fixed incorrect relative import paths and implicit any types.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/company-profile/components/CompanyAddress.tsx
 */
import React from "react";
import { Form, Row, Col } from "react-bootstrap";
import countries from "../../../../utils/countries";
// [2026-02-07] Import states utility for country-specific state dropdowns
import { getStatesForCountry, hasStates, getStateLabel } from "../../../../utils/states";

// [2025-12-29] Parameter type fix
export const CompanyAddress: React.FC<{ formik: any }> = ({ formik }) => {
  return (
    <div className="mt-3">
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <Form.Label className="field-name mb-0">Address</Form.Label>
          <span className="text-muted ms-1 small">(address or country required for broadcasts)</span>
        </Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("address1")} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Address 2</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("address2")} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">City</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("city")} /></Col>
      </Row>
      {/* [2026-02-07] Country moved before State for better UX */}
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <Form.Label className="field-name mb-0">Country</Form.Label>
          <span className="text-muted ms-1 small">(address or country required for broadcasts)</span>
        </Col>
        <Col md={8}>
          <Form.Select
            className="field-select"
            {...formik.getFieldProps("country")}
            onChange={(e) => {
              formik.handleChange(e);
              // Clear state when country changes if new country has different states
              if (hasStates(e.target.value) !== hasStates(formik.values.country)) {
                formik.setFieldValue("state", "");
              }
            }}
          >
            <option value="">Select country</option>
            {countries.map((c: string) => <option key={c} value={c}>{c}</option>)}
          </Form.Select>
        </Col>
      </Row>
      {/* [2026-02-07] State field - dropdown for countries with states, text input for others */}
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <Form.Label className="field-name mb-0">
            {formik.values.country ? getStateLabel(formik.values.country) : "State"}
          </Form.Label>
        </Col>
        <Col md={8}>
          {hasStates(formik.values.country) ? (
            <Form.Select
              className="field-select"
              {...formik.getFieldProps("state")}
            >
              <option value="">Select {getStateLabel(formik.values.country).toLowerCase()}</option>
              {getStatesForCountry(formik.values.country).map((s: string) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Form.Select>
          ) : (
            // [2026-02-07] Disable state field when country doesn't have predefined states
            <Form.Control
              className="field-input"
              placeholder={formik.values.country ? "Not applicable" : "Select country first"}
              {...formik.getFieldProps("state")}
              disabled={true}
            />
          )}
        </Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Zip</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("zip")} /></Col>
      </Row>
    </div>
  );
};
